/** Type guard for `Record<string, unknown>` — true for non-null plain objects (not arrays). */
export const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

/**
 * Read a field from `obj` by trying each key in order; coerces the first
 * non-null match to a string. Returns `''` when none match. Used by parsers
 * that tolerate either snake_case or camelCase wire shapes:
 *
 *   pickString(raw, 'firstName', 'first_name')  // first hit wins
 */
export const pickString = (obj: Record<string, unknown>, ...keys: string[]): string => {
  for (const key of keys) {
    const value = obj[key]
    if (value !== null && value !== undefined) return String(value)
  }
  return ''
}

/**
 * Read a numeric field from `obj` by trying each key in order. Returns `null`
 * when no key matches (or all matches are null/undefined). Coerces via
 * `Number()` — non-numeric inputs become NaN, which the caller can decide
 * to surface or clamp.
 */
export const pickNumber = (obj: Record<string, unknown>, ...keys: string[]): number | null => {
  for (const key of keys) {
    const value = obj[key]
    if (value !== null && value !== undefined) return Number(value)
  }
  return null
}

/**
 * Stringify an unknown value for display in a label/value row.
 *
 *   null / undefined / ''      → "N/A"
 *   true / false               → "Yes" / "No"
 *   number                     → its string
 *   primitive[]                → "a, b, c"
 *   object[]                   → "N items"
 *   plain object               → "N fields"  (or "N/A" when empty)
 *   anything else              → String(value)
 *
 * Use this only where the surface is a generic detail-row renderer and the
 * field type is unknown at compile time (master-data detail tabs, audit
 * payloads, etc.). For typed values prefer the dedicated formatter
 * (formatMoney, formatDate, ...).
 */
export const formatDisplayValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'N/A'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') return String(value)
  if (Array.isArray(value)) {
    if (!value.length) return 'N/A'
    if (value.every((item) => ['string', 'number', 'boolean'].includes(typeof item))) {
      return value.join(', ')
    }
    return `${value.length} items`
  }
  if (isPlainObject(value)) {
    const keys = Object.keys(value)
    return keys.length ? `${keys.length} fields` : 'N/A'
  }
  return String(value)
}
